import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchSupplierOffers, fetchSupplierConversations } from '../../lib/consoleSupplierActivity';

// Offers and conversations on the supplier file. Every link stays inside
// /admin2 (owner, 2026-10-11).

const when = (iso, isAr) => (iso ? new Date(iso).toLocaleDateString(isAr ? 'ar-SA' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

// The trader's decision lives on offers.status; visibility is a separate
// column (golden rule 2). Both are shown, never merged into one label.
const DECISION = {
  pending: { ar: 'بانتظار التاجر', en: 'Awaiting trader', cls: 'warn' },
  accepted: { ar: 'قبله التاجر', en: 'Accepted', cls: 'ok' },
  rejected: { ar: 'رفضه التاجر', en: 'Rejected', cls: 'neutral' },
};
const HELD = {
  unverified_supplier: { ar: 'محجوب حتى التوثيق', en: 'Held until verified' },
  managed_raw: { ar: 'داخلي — لا يراه التاجر', en: 'Internal — never shown' },
};

export default function SupplierActivity({ supplierId, factoryId, isAr }) {
  const nav = useNavigate();
  const [offers, setOffers] = useState(null);
  const [convos, setConvos] = useState(null);

  useEffect(() => {
    let alive = true;
    setOffers(null); setConvos(null);
    fetchSupplierOffers(supplierId).then((r) => { if (alive) setOffers(r); });
    fetchSupplierConversations(supplierId, factoryId).then((r) => { if (alive) setConvos(r); });
    return () => { alive = false; };
  }, [supplierId, factoryId]);

  const row = { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--ac-line)', cursor: 'pointer' };
  const sub = { fontSize: 12, color: 'var(--ac-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

  return (
    <>
      <div className="ac-card">
        <p className="ac-card-title">{isAr ? 'عروضه' : 'Offers'}{offers && !offers.failed ? <span style={{ color: 'var(--ac-faint)', fontWeight: 500, marginInlineStart: 6 }}>{offers.rows.length}</span> : null}</p>
        {offers === null ? <div className="ac-skel" style={{ height: 50 }} />
          : offers.failed ? <p className="acv-modal-warn">{isAr ? 'تعذّر تحميل العروض.' : 'Could not load offers.'}</p>
          : offers.rows.length === 0 ? <p className="ac-placeholder">{isAr ? 'لم يقدّم أي عرض.' : 'No offers yet.'}</p>
          : offers.rows.map((o) => {
            const dec = DECISION[o.status];
            const held = o.managed_visibility === 'admin_only' ? (HELD[o.visibility_reason] || { ar: 'محجوب عن التاجر', en: 'Held from trader' }) : null;
            const r = o.request || {};
            return (
              <div key={o.id} role="button" style={row} onClick={() => nav(`/admin2/requests/${o.request_id}`)}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ac-ink)' }}>
                    {(isAr ? (r.title_ar || r.title_en) : (r.title_en || r.title_ar)) || (isAr ? 'طلب' : 'Request')}
                  </div>
                  <div style={sub}>{[r.request_ref, o.price != null ? `${o.price} ${o.currency || ''}`.trim() : ''].filter(Boolean).join(' · ')}</div>
                </div>
                {held && <span className="ac-badge warn">{isAr ? held.ar : held.en}</span>}
                {dec && <span className={`ac-badge ${dec.cls}`}>{isAr ? dec.ar : dec.en}</span>}
                <span style={{ fontSize: 11.5, color: 'var(--ac-faint)', whiteSpace: 'nowrap' }}>{when(o.created_at, isAr)}</span>
              </div>
            );
          })}
      </div>

      <div className="ac-card">
        <p className="ac-card-title">{isAr ? 'محادثاته مع التجار' : 'Conversations with traders'}{convos && !convos.failed ? <span style={{ color: 'var(--ac-faint)', fontWeight: 500, marginInlineStart: 6 }}>{convos.rows.length}</span> : null}</p>
        {convos === null ? <div className="ac-skel" style={{ height: 50 }} />
          : convos.failed ? <p className="acv-modal-warn">{isAr ? 'تعذّر تحميل المحادثات.' : 'Could not load conversations.'}</p>
          : convos.rows.length === 0 ? <p className="ac-placeholder">{isAr ? 'لا محادثات.' : 'No conversations.'}</p>
          : convos.rows.map((c) => (
            <div key={`${c.channel}:${c.trader_id}`} role="button" style={row}
              onClick={() => nav(`/admin2/conversations?${new URLSearchParams({ channel: c.channel, trader: c.trader_id, counterpart: c.counterpart_id })}`)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ac-ink)' }}>{c.trader_name || (isAr ? 'تاجر' : 'Trader')}</div>
                <div style={sub}>{c.last_from_trader ? (isAr ? 'التاجر: ' : 'Trader: ') : ''}{c.last_preview}</div>
              </div>
              {c.channel === 'factory' && <span className="ac-badge info">{isAr ? 'عبر صفحة المصنع' : 'Via factory page'}</span>}
              <span style={{ fontSize: 11.5, color: 'var(--ac-faint)' }}>{c.message_count}</span>
              <span style={{ fontSize: 11.5, color: 'var(--ac-faint)', whiteSpace: 'nowrap' }}>{when(c.last_at, isAr)}</span>
            </div>
          ))}
      </div>
    </>
  );
}
